import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { SiteService } from '../config/site.service';
import { HttpClientService } from '../http/http-client.service';
import { MessageService } from '../message/message.service';
import { WsMessageBean } from '../models/ws.message-bean.model';
import { WsQueryBean } from '../models/ws.query-bean.model';

@Injectable({
    providedIn: 'root',
})
export class WebSocketService {

    private webSocket: WebSocket;

    // Time to wait before attempting reconnect (after close)
    private readonly reconnectInterval: number = 1000;
    // Whether WebSocket was forced to close by this client
    private forcedClose: boolean = false;
    // Should only be used to read WebSocket readyState
    private readyState: number = -1;

    private isConnected: Subject<boolean> = new Subject();
    private messageSubject: Subject<WsMessageBean> = new Subject();
    private queryBeanQueue: WsQueryBean[] = [];

    constructor(private mySiteService: SiteService,
                private myMessageService: MessageService,
                private myHttpClientService: HttpClientService,
    ) {
    }

    public connectWebSocket(reconnectAttempt: boolean = false): void {
        this.forcedClose = false;
        if (this.readyState !== WebSocket.CONNECTING && this.readyState !== WebSocket.OPEN) {
            this.executeConnectWebsocket(reconnectAttempt);
        } else {
            console.warn('WebSocket is already connected!');
        }
    }

    public closeWebsocket(): void {
        this.forcedClose = true;
        if (this.webSocket != null) {
            this.webSocket.close();
        }
        this.readyState = -1;
    }

    public sendDataToWebsocket(queryBean: WsQueryBean): void {
        if (this.readyState === WebSocket.OPEN && this.webSocket) {
            this.webSocket.send(JSON.stringify(queryBean));
        } else if (this.readyState === WebSocket.CONNECTING) {
            this.queryBeanQueue.push(queryBean);
        } else {
            console.error('WS send data -> WebSocket.CLOSED');
            // der ws muss geschlossen sein, also neu verbinden
            this.queryBeanQueue.push(queryBean);
            this.connectWebSocket();
        }
    }

    public isWebSocketConnected(): Observable<boolean> {
        return this.isConnected.asObservable().pipe(distinctUntilChanged());
    }

    public isWebSocketConnectedSnapshot(): boolean {
        return this.readyState === WebSocket.OPEN;
    }

    public getWsMessages(): Observable<WsMessageBean> {
        return this.messageSubject.asObservable();
    }

    private executeConnectWebsocket(reconnectAttempt: boolean): void {
        this.webSocket = new WebSocket(this.mySiteService.getConfigKey('websocket'));
        this.readyState = WebSocket.CONNECTING;

        this.webSocket.onopen = (): void => {
            this.readyState = WebSocket.OPEN;
            reconnectAttempt = false;
            this.isConnected.next(true);
        };

        this.webSocket.onclose = (): void => {
            this.isConnected.next(false);
            this.webSocket = null;
            if (this.forcedClose) {
                this.readyState = WebSocket.CLOSED;
            } else {
                reconnectAttempt = true;
                if (reconnectAttempt) {
                    setTimeout(() => {
                        this.executeConnectWebsocket(true);
                    }, this.reconnectInterval);
                }
            }
        };

        this.webSocket.onmessage = (event: MessageEvent): void => {
            const tmp: WsMessageBean = JSON.parse(event.data);
            this.messageHandler(tmp);
        };

        this.webSocket.onerror = (): void => {
            this.myMessageService.error('Error WS', 'Ws Connection Error');
        };
    }

    private sendConnectToken(token: string): void {
        this.myHttpClientService.post(`notification/connect/${token}`, {})
            .subscribe(() => {
                if (this.queryBeanQueue.length > 0) {
                    for (let i: number = 0; i < this.queryBeanQueue.length; i++) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        this.webSocket.send(this.queryBeanQueue[ i ] as any);
                        this.myMessageService.info('Error!', `WS resending queued Event ${this.queryBeanQueue[ i ].type}`);
                    }
                    this.queryBeanQueue = [];
                }
            });
    }

    private messageHandler(messageBean: WsMessageBean): void {
        if (messageBean.type === 'APP') {
            // die listener benachrichtigen
            if (messageBean.subtype === 'core.connect-token') {
                this.sendConnectToken(messageBean.token);
            }
        }

        this.messageSubject.next(messageBean);
    }
}
